/**
 * ElectricalInstallationWizard — cadastro e edição de instalação elétrica em etapas (issue #364, D10).
 *
 * Criação: a instalação nasce ao salvar a etapa 1; inspeções e documentos dependem do id.
 * Edição: as mesmas etapas, com navegação livre.
 * Leitura (sem `.update`): as mesmas etapas, sem nada para gravar.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, LinearProgress, Paper, Typography } from '@mui/material';
import { useCompanyContext } from '../../../../hooks/useCompanyContext';
import { electricalInstallationService } from '../../../../services/electricalInstallationService';
import type { Department } from '../../../../types/department';
import { ConfirmDialog } from './ConfirmDialog';
import {
  ELECTRICAL_WIZARD_STEPS,
  EMPTY_IDENTIFICATION,
  type IdentificationErrors,
  type IdentificationValues,
  type ResultadoDoEnvio,
  type StepHandle,
} from './ElectricalInstallationWizard.types';
import { errorMessage, identificationToPayload, validateIdentification } from './electricalWizardValidation';
import { IdentificationStep } from './steps/IdentificationStep';
import { ETAPAS_DE_REGISTRO } from './steps/registroDasEtapas';

export interface ElectricalInstallationWizardProps {
  /** Presente na edição e na leitura; ausente na criação (o id nasce ao salvar a etapa 1). */
  installationId?: string;
  initialValues?: IdentificationValues;
  departments: Department[];
  /** Quem pode mexer na instalação e no que há dentro dela (`electrical_installation.update`). */
  canUpdate: boolean;
  /** Avisa a empresa escolhida na etapa 1, para quem carrega só os setores dela. */
  onCompanyChange?: (companyId: string) => void;
  onFinish: () => void;
  onCancel: () => void;
  /** Quem cria sem poder editar: a instalação foi criada e a tela segue na visão de leitura dela. */
  onCreatedReadOnly: (installationId: string) => void;
}

const WHY_BY_STEP = [
  'Salvar guarda os dados da instalação e leva você às inspeções e manutenções.',
  'Salvar guarda o registro que você digitou, se houver, e leva você aos documentos técnicos.',
  'Concluir envia o documento que você preencheu, se houver, e leva você de volta à lista de instalações.',
];

const WHY_READ_ONLY = [
  'Você está consultando: seu acesso permite ver a instalação, não alterá-la.',
  'Você está consultando as inspeções e manutenções desta instalação.',
  'Você está consultando os documentos técnicos desta instalação.',
];

export const ElectricalInstallationWizard: React.FC<ElectricalInstallationWizardProps> = ({
  installationId: initialInstallationId,
  initialValues = EMPTY_IDENTIFICATION,
  departments,
  canUpdate,
  onCompanyChange,
  onFinish,
  onCancel,
  onCreatedReadOnly,
}) => {
  const [installationId, setInstallationId] = useState<string | null>(initialInstallationId ?? null);
  const [activeStep, setActiveStep] = useState(0);
  const [values, setValues] = useState<IdentificationValues>(initialValues);
  const [savedValues, setSavedValues] = useState<IdentificationValues>(initialValues);
  const [errors, setErrors] = useState<IdentificationErrors>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [stepDirty, setStepDirty] = useState(false);
  const [leaving, setLeaving] = useState<(() => void) | null>(null);
  const stepRef = useRef<StepHandle>(null);

  const isEditing = Boolean(initialInstallationId);
  const readOnly = isEditing && !canUpdate;
  // Sem empresa ativa no seletor do cabeçalho, quem cadastra precisa dizer de quem é a instalação.
  const { shouldShowCompanyField } = useCompanyContext();
  const askCompany = !isEditing && shouldShowCompanyField;
  // Salva a instalação, a empresa dona fica travada: o servidor ignora a troca na atualização.
  const companyLocked = installationId !== null;
  const total = ELECTRICAL_WIZARD_STEPS.length;
  const isLastStep = activeStep === total - 1;
  const dirty = readOnly
    ? false
    : activeStep === 0
      ? JSON.stringify(values) !== JSON.stringify(savedValues)
      : stepDirty;

  useEffect(() => {
    onCompanyChange?.(values.companyId);
  }, [onCompanyChange, values.companyId]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const handleDirtyChange = useCallback((value: boolean) => setStepDirty(value), []);
  const handleSaved = useCallback((message: string) => setNotice(message), []);

  /** Executa a saída já; se há dados sem salvar, pergunta antes. */
  const leaveOrAsk = (action: () => void) => {
    if (dirty) setLeaving(() => action);
    else action();
  };

  const goToStep = (index: number) => {
    setError(null);
    setNotice(null);
    setStepDirty(false);
    setActiveStep(index);
  };

  const discardAndLeave = () => {
    if (activeStep === 0) setValues(savedValues);
    const action = leaving;
    setLeaving(null);
    action?.();
  };

  /** Grava a etapa 1. Devolve `continuar: false` quando algo impede de seguir para a etapa 2. */
  const saveIdentification = async (): Promise<{ continuar: boolean; criadaSemEdicao?: string }> => {
    const found = validateIdentification(values, askCompany && !companyLocked);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setError('Corrija os campos marcados para continuar.');
      return { continuar: false };
    }

    setBusy(true);
    setError(null);
    try {
      const saved = installationId
        ? await electricalInstallationService.updateInstallation(installationId, identificationToPayload(values, false))
        : await electricalInstallationService.createInstallation(identificationToPayload(values));
      const savedId = String(saved.id);
      // A classe de tensão é do servidor (D8): a tela guarda o que ele devolveu.
      const next: IdentificationValues = {
        ...values,
        companyId: values.companyId || (saved.companyId ? String(saved.companyId) : ''),
        name: saved.name,
        tag: saved.tag ?? '',
        typeDescription: saved.typeDescription ?? '',
        voltageClass: saved.summary?.voltageClass ?? values.voltageClass,
      };
      setValues(next);
      setSavedValues(next);
      setInstallationId(savedId);
      if (!installationId && !canUpdate) return { continuar: false, criadaSemEdicao: savedId };
      return { continuar: true };
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível salvar a instalação.'));
      return { continuar: false };
    } finally {
      setBusy(false);
    }
  };

  /** Na última etapa, um documento digitado e não enviado é enviado; se não der, pergunta antes de descartar. */
  const finishLastStep = async () => {
    if (readOnly || !stepDirty) {
      onFinish();
      return;
    }
    setBusy(true);
    // Sem a etapa para responder, o que foi digitado não foi enviado.
    let resultado: ResultadoDoEnvio;
    try {
      resultado = (await stepRef.current?.salvarPendente()) ?? 'incompleto';
    } finally {
      setBusy(false);
    }
    if (resultado === 'enviado') onFinish();
    // Recusa do servidor: a etapa já mostra a frase dele, e o usuário fica onde está para corrigir.
    else if (resultado === 'incompleto') setLeaving(() => onFinish);
  };

  const handlePrimary = async () => {
    if (isLastStep) {
      await finishLastStep();
      return;
    }
    if (readOnly) {
      goToStep(activeStep + 1);
      return;
    }

    setBusy(true);
    let proceed: boolean;
    try {
      if (activeStep === 0) {
        const result = await saveIdentification();
        if (result.criadaSemEdicao) {
          onCreatedReadOnly(result.criadaSemEdicao);
          return;
        }
        proceed = result.continuar;
      } else {
        proceed = ((await stepRef.current?.salvarPendente()) ?? 'enviado') === 'enviado';
      }
    } finally {
      setBusy(false);
    }
    if (!proceed) return;

    const wasIdentification = activeStep === 0;
    goToStep(activeStep + 1);
    if (wasIdentification) setNotice('Dados da instalação salvos.');
  };

  const handleChange = (patch: Partial<IdentificationValues>) => {
    setValues((previous) => ({ ...previous, ...patch }));
    setErrors((previous) => {
      const next = { ...previous };
      (Object.keys(patch) as Array<keyof IdentificationValues>).forEach((key) => delete next[key]);
      return next;
    });
  };

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <IdentificationStep
          values={values}
          errors={errors}
          departments={departments}
          askCompany={askCompany}
          companyLocked={companyLocked}
          disabled={busy || readOnly}
          onChange={handleChange}
        />
      );
    }
    if (!installationId) return null;
    const StepComponent = ETAPAS_DE_REGISTRO[activeStep - 1];
    return (
      <StepComponent
        ref={stepRef}
        installationId={installationId}
        canEdit={canUpdate}
        onDirtyChange={handleDirtyChange}
        onSaved={handleSaved}
      />
    );
  };

  const primaryLabel = isLastStep
    ? readOnly
      ? 'Voltar à lista'
      : 'Concluir cadastro'
    : readOnly
      ? 'Próxima etapa'
      : 'Salvar e continuar';
  const why = (readOnly ? WHY_READ_ONLY : WHY_BY_STEP)[activeStep];

  return (
    <Box>
      <Box
        component="nav"
        aria-label="Etapas do cadastro"
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${total}, 1fr)` }, gap: 1, mb: 1 }}
      >
        {ELECTRICAL_WIZARD_STEPS.map((step, index) => {
          const reachable = isEditing || installationId !== null || index === 0;
          const state = index === activeStep ? 'Etapa atual' : index < activeStep ? 'Feita' : 'Falta';
          return (
            <Button
              key={step.key}
              variant={index === activeStep ? 'contained' : 'outlined'}
              color="primary"
              disabled={!reachable || busy}
              aria-current={index === activeStep ? 'step' : undefined}
              onClick={() => index !== activeStep && leaveOrAsk(() => goToStep(index))}
              sx={{ minHeight: 56, flexDirection: 'column', alignItems: 'flex-start', textTransform: 'none', textAlign: 'left' }}
            >
              <Typography component="span" sx={{ fontSize: '1rem' }}>
                Etapa {index + 1} de {total} · {state}
              </Typography>
              <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 700 }}>
                {step.label}
              </Typography>
            </Button>
          );
        })}
      </Box>
      <LinearProgress
        variant="determinate"
        value={((activeStep + 1) / total) * 100}
        aria-label="Progresso do cadastro"
        sx={{ mb: 3, height: 8, borderRadius: 4 }}
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        {renderStep()}
      </Paper>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="body1">{why}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!readOnly && (
            <Button variant="text" onClick={() => leaveOrAsk(onCancel)} disabled={busy} sx={{ minHeight: 44 }}>
              Sair do cadastro
            </Button>
          )}
          {activeStep > 0 && (
            <Button
              variant="text"
              onClick={() => leaveOrAsk(() => goToStep(activeStep - 1))}
              disabled={busy}
              sx={{ minHeight: 44 }}
            >
              Voltar
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handlePrimary}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={20} color="inherit" /> : undefined}
            sx={{ minHeight: 44 }}
          >
            {primaryLabel}
          </Button>
        </Box>
      </Box>

      <ConfirmDialog
        open={leaving !== null}
        title="Sair sem salvar?"
        message="Você digitou dados que ainda não foram salvos. Se sair agora, eles se perdem."
        confirmLabel="Sair sem salvar"
        cancelLabel="Continuar editando"
        onConfirm={discardAndLeave}
        onCancel={() => setLeaving(null)}
      />
    </Box>
  );
};

export default ElectricalInstallationWizard;
